import { parseISO } from 'date-fns/parseISO';
import type {
  LocationType,
  PropertyRowDataItem,
} from 'uplisting-frontend/services/calendar/-internals/types';
import { utcDate } from 'uplisting-frontend/utils/date';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import BookingModel from 'uplisting-frontend/models/booking';

export function locationOf(record: PropertyRowDataItem): LocationType {
  return record.multiUnit || record.property;
}

export function clashes(
  location: LocationType,
  record: PropertyRowDataItem,
  other: PropertyRowDataItem,
): boolean {
  if (location instanceof MultiUnitModel) {
    return true;
  }

  if (location.multiUnits?.length) {
    return false;
  }

  return record instanceof BookingModel && other instanceof BookingModel;
}

export function stayOf(record: PropertyRowDataItem): [Date, Date] | undefined {
  if (!record.checkIn || !record.checkOut) {
    return;
  }

  return [
    utcDate(parseISO(record.checkIn)),
    utcDate(parseISO(record.checkOut)),
  ];
}

function recordsAt(location: LocationType): PropertyRowDataItem[] {
  const isUnit = location instanceof MultiUnitModel;

  const records: PropertyRowDataItem[] = [
    ...location.bookings,
    ...location.bookingRequests,
    ...location.unavailabilities,
    ...location.enquiries,
  ];

  return records.filter((record) => {
    if (!record.id || (record instanceof BookingModel && record.isCancelled)) {
      return false;
    }

    return isUnit ? record.multiUnit?.id === location.id : !record.multiUnit;
  });
}

function occupies(
  record: PropertyRowDataItem,
  start: Date,
  end: Date,
): boolean {
  const stay = stayOf(record);

  if (!stay) {
    return false;
  }

  const [stayStart, stayEnd] = stay;

  return end > stayStart && start < stayEnd;
}

function shareANight(
  record: PropertyRowDataItem,
  other: PropertyRowDataItem,
): boolean {
  const stay = stayOf(record);

  return !!stay && occupies(other, ...stay);
}

// by record, as a block and a booking can share an id
export function occupantsOf(
  location: LocationType,
  start: Date,
  end: Date,
  exclude: PropertyRowDataItem[] = [],
): PropertyRowDataItem[] {
  return recordsAt(location).filter(
    (record) => !exclude.includes(record) && occupies(record, start, end),
  );
}

export function clashesOf(record: PropertyRowDataItem): PropertyRowDataItem[] {
  const stay = stayOf(record);

  if (!stay) {
    return [];
  }

  const location = locationOf(record);

  const others = occupantsOf(location, ...stay).filter(
    (other) => other !== record,
  );

  return others.filter(
    (other) =>
      clashes(location, record, other) ||
      others.some((third) => third !== other && shareANight(other, third)),
  );
}
